/**
 * NotesPanel - Right Panel (Preview + Graph + Timeline + Tools)
 *
 * COPIED DIRECTLY from intelligence-studio/index.jsx (lines 7020-7884)
 * No logic changes. Only relocated for SRP + maintainability.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { getApiBaseUrl } from '../../../../config/runtimeUrls';
import { useSocketEmit, useSocketEvent, useSocketStatus } from '../../../../hooks/useSocket';

import {
  Layers,
  FileText,
  Check,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Network,
  AlertCircle,
  File,
  PanelRightClose,
  Map,
  Zap,
  Target,
  Scale,
  Download,
  Maximize2,
} from 'lucide-react';

import intelligenceStudioApi from '../../../../api/intelligenceStudioApi';
import { UnifiedDocumentViewer } from '@/components/DocumentViewer/UnifiedDocumentViewer';
import { getFileTypeTooltip } from '@/pages/intelligence-studio/utils/fileUtils';
import { traceFetch } from '@/pages/intelligence-studio/utils/traceContext';
import { authService } from '../../../../services/auth/AuthService';

// ✅ View content components (moved out of the old monolith)
import {
  ActionsViewContent,
  CrossDomainViewContent,
  IntelligenceViewContent,
} from '@/pages/intelligence-studio/views/agi/agiViews';
import {
  GraphViewContent,
  ScenariosViewContent,
  TimelineViewContent,
  ToolsContent,
} from '@/pages/intelligence-studio/views/srlu/srluViews';

// ==========================================
// RIGHT: FILE PREVIEW & GRAPH PANEL
// ==========================================

const NotesPanel = ({
  notebook,
  collapsed,
  onToggleCollapse,
  selectedFile,
  onClearSelectedFile,
  onBuildTimeline,
  onMapClauses,
  targetPage,
  targetText,
  width = 320,
}) => {
  // ✅ ENTERPRISE: Always prefer the freshest file object from `notebook.files`
  // (prevents stale selectedFile causing trust/state contradictions after refresh/stream updates).
  const selectedFileId = selectedFile?.fileId || selectedFile?.id;
  const freshSelectedFile = useMemo(() => {
    if (!selectedFileId) return selectedFile;
    const files = Array.isArray(notebook?.files) ? notebook.files : [];
    const found = files.find(f => (f.fileId || f.id) === selectedFileId);
    return found || selectedFile;
  }, [selectedFileId, notebook?.files]);
  const [activeTab, setActiveTab] = useState('preview'); // preview | roles | graph | scenarios | timeline | tools

  // ✅ FINAL UX CONTRACT: Notebook-level gating derived from server-side truth
  const notebookTrustState = String(notebook?.userTrustSummary?.state || '').toLowerCase();
  const notebookIsReady = notebookTrustState === 'trusted' || notebookTrustState === 'readable';
  const notebookIsProcessing =
    notebookTrustState === 'processing' || notebookTrustState === 'reading'; // backward compat
  const notebookIsLowQuality = notebookTrustState === 'low_quality';
  const analyzedCount = notebook?.userTrustSummary?.analyzed ?? null;
  const analyzedTotal = notebook?.userTrustSummary?.total ?? null;

  // ✅ Addiction layer: earned unlock moment for right-column tabs
  const prevNotebookReadyRef = useRef(notebookIsReady);
  const [justUnlocked, setJustUnlocked] = useState(false);

  useEffect(() => {
    const prev = prevNotebookReadyRef.current;
    if (prev === false && notebookIsReady === true) {
      setJustUnlocked(true);
      const t = setTimeout(() => setJustUnlocked(false), 1200);
      t?.unref?.();
      return () => clearTimeout(t);
    }
    prevNotebookReadyRef.current = notebookIsReady;
    return undefined;
  }, [notebookIsReady]);

  // Switch to preview tab when a citation is clicked
  useEffect(() => {
    if (targetPage || selectedFile?.fileId || selectedFile?.id) {
      setActiveTab('preview');
    }
  }, [targetPage, selectedFile?.fileId, selectedFile?.id]);
  const [graphData, setGraphData] = useState(null);
  const [timelineData, setTimelineData] = useState(null);
  const [roleViewData, setRoleViewData] = useState(null);
  const [scenariosData, setScenariosData] = useState(null);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [isLoadingTimeline, setIsLoadingTimeline] = useState(false);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isLoadingScenarios, setIsLoadingScenarios] = useState(false);

  // Load data when tab changes
  useEffect(() => {
    // Hard gate non-preview tabs until notebook is Trusted/Readable (server-side truth)
    if (!notebookIsReady) return;
    if (activeTab === 'graph' && notebook?.id && !graphData) loadGraph();
    if (activeTab === 'timeline' && notebook?.id && !timelineData) loadTimeline();
    if (activeTab === 'roles' && notebook?.id && !roleViewData) loadRoleView();
    if (activeTab === 'scenarios' && notebook?.id && !scenariosData) loadScenarios();
  }, [activeTab, notebook?.id, notebookIsReady]);

  const loadGraph = async () => {
    if (!notebook?.id) return;
    setIsLoadingGraph(true);
    try {
      const response = await intelligenceStudioApi.getKnowledgeGraph(notebook.id);
      setGraphData(response?.data?.data || response?.data || response);
    } catch (err) {
      console.error('Failed to load graph:', err);
    } finally {
      setIsLoadingGraph(false);
    }
  };

  const rebuildGraph = async () => {
    if (!notebook?.id) return;
    setIsLoadingGraph(true);
    try {
      console.log('🔨 Rebuilding graph for notebook:', notebook.id);
      const response = await intelligenceStudioApi.rebuildGraph(notebook.id);
      console.log('Graph rebuild response:', response);

      // After rebuild, reload the graph
      const graphResponse = await intelligenceStudioApi.getKnowledgeGraph(notebook.id);
      setGraphData(graphResponse?.data?.data || graphResponse?.data || graphResponse);
    } catch (err) {
      console.error('Failed to rebuild graph:', err);
      alert('Failed to rebuild graph: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoadingGraph(false);
    }
  };

  const loadTimeline = async () => {
    if (!notebook?.id) return;
    setIsLoadingTimeline(true);
    try {
      const response = await intelligenceStudioApi.getTimelineData(notebook.id);
      setTimelineData(response?.data?.data || response?.data || response);
    } catch (err) {
      console.error('Failed to load timeline:', err);
    } finally {
      setIsLoadingTimeline(false);
    }
  };

  const loadRoleView = async () => {
    if (!notebook?.id) return;
    setIsLoadingRoles(true);
    try {
      const response = await intelligenceStudioApi.getRoleView(notebook.id);
      setRoleViewData(response?.data?.data || response?.data || response);
    } catch (err) {
      console.error('Failed to load role view:', err);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const extractSRL = async () => {
    if (!notebook?.id) return;
    setIsLoadingRoles(true);
    try {
      console.log('🧠 Extracting SRL for notebook:', notebook.id);
      const response = await intelligenceStudioApi.extractSRL(notebook.id);
      console.log('SRL extraction response:', response);

      // After extraction, load the role view
      const roleResponse = await intelligenceStudioApi.getRoleView(notebook.id);
      setRoleViewData(roleResponse?.data?.data || roleResponse?.data || roleResponse);
    } catch (err) {
      console.error('Failed to extract SRL:', err);
      alert('Failed to extract SRL: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const loadScenarios = async () => {
    if (!notebook?.id) return;
    setIsLoadingScenarios(true);
    try {
      // First run intelligence analysis to generate scenarios
      await intelligenceStudioApi.runIntelligenceAnalysis(notebook.id, false);
      // Then get the scenarios
      const response = await intelligenceStudioApi.getIntelligenceScenarios(notebook.id);
      setScenariosData(response?.data?.data || response?.data || response);
    } catch (err) {
      console.error('Failed to load scenarios:', err);
    } finally {
      setIsLoadingScenarios(false);
    }
  };

  // Intelligence state
  const [intelligenceData, setIntelligenceData] = useState(null);
  const [actionsData, setActionsData] = useState(null);
  const [crossDomainData, setCrossDomainData] = useState(null);
  const [isLoadingIntelligence, setIsLoadingIntelligence] = useState(false);
  const [isLoadingActions, setIsLoadingActions] = useState(false);
  const [isLoadingCrossDomain, setIsLoadingCrossDomain] = useState(false);

  // Load intelligence data when tab changes
  useEffect(() => {
    // Hard gate non-preview tabs until notebook is Trusted/Readable (server-side truth)
    if (!notebookIsReady) return;
    if (activeTab === 'intelligence' && notebook?.id && !intelligenceData)
      runIntelligenceAnalysis();
    if (activeTab === 'actions' && notebook?.id && !actionsData) loadActions();
    if (activeTab === 'crossdomain' && notebook?.id && !crossDomainData) loadCrossDomain();
  }, [activeTab, notebook?.id, notebookIsReady]);

  const runIntelligenceAnalysis = async () => {
    if (!notebook?.id) return;
    setIsLoadingIntelligence(true);
    try {
      console.log('🧠 Running AGI intelligence analysis...');
      const response = await intelligenceStudioApi.runIntelligenceAnalysis(notebook.id);
      setIntelligenceData(response?.data?.data || response?.data || response);
      // Also update actions and scenarios after intelligence run
      if (response?.data?.data?.action_items) {
        setActionsData({
          actions: [
            ...(response.data.data.action_items.legal_actions || []).map(a => ({
              ...a,
              domain: 'legal',
            })),
            ...(response.data.data.action_items.fmcg_actions || []).map(a => ({
              ...a,
              domain: 'fmcg',
            })),
            ...(response.data.data.action_items.banking_actions || []).map(a => ({
              ...a,
              domain: 'banking',
            })),
          ],
        });
      }
      if (response?.data?.data?.scenarios) {
        setScenariosData({ scenarios: response.data.data.scenarios });
      }
    } catch (err) {
      console.error('Failed to run intelligence analysis:', err);
    } finally {
      setIsLoadingIntelligence(false);
    }
  };

  const loadActions = async () => {
    if (!notebook?.id) return;
    setIsLoadingActions(true);
    try {
      const response = await intelligenceStudioApi.getIntelligenceActions(notebook.id);
      setActionsData(response?.data || response);
    } catch (err) {
      console.error('Failed to load actions:', err);
    } finally {
      setIsLoadingActions(false);
    }
  };

  const loadCrossDomain = async () => {
    if (!notebook?.id) return;
    setIsLoadingCrossDomain(true);
    try {
      const response = await intelligenceStudioApi.getCrossDomainInsights(notebook.id);
      setCrossDomainData(response?.data || response);
    } catch (err) {
      console.error('Failed to load cross-domain:', err);
    } finally {
      setIsLoadingCrossDomain(false);
    }
  };

  const tabs = [
    {
      id: 'preview',
      icon: <FileText className="h-4 w-4" />,
      label: 'Preview',
      alwaysEnabled: true,
    },
    { id: 'intelligence', icon: <Layers className="h-4 w-4" />, label: 'Intel' },
    { id: 'actions', icon: <Target className="h-4 w-4" />, label: 'Actions' },
    { id: 'crossdomain', icon: <Network className="h-4 w-4" />, label: 'Links' },
    { id: 'scenarios', icon: <Zap className="h-4 w-4" />, label: 'Scenarios' },
    { id: 'graph', icon: <Scale className="h-4 w-4" />, label: 'Graph' },
    { id: 'timeline', icon: <Map className="h-4 w-4" />, label: 'Timeline' },
  ];

  const gateMessage = notebookIsProcessing
    ? 'Processing: extracting text, structure, and key signals…'
    : notebookIsLowQuality
      ? 'Scan is too blurry for accurate analysis. Please upload a clearer version.'
      : null;

  // ✅ Strict Tab × Trust unlock: tabs only appear when earned (Preview is always available).
  const visibleTabs = notebookIsReady ? tabs : tabs.filter(t => t.alwaysEnabled);
  const lockedTabs = tabs.filter(t => !t.alwaysEnabled);

  // If tabs get hidden (processing/low-quality), force the user back to Preview.
  useEffect(() => {
    const ids = new Set(visibleTabs.map(t => t.id));
    if (!ids.has(activeTab)) setActiveTab('preview');
  }, [visibleTabs, activeTab]);

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-neutral-800 overflow-hidden">
      {/* Header - h-10 (40px) for perfect alignment with all columns */}
      <div className="flex items-center justify-between px-3 h-10 border-b border-slate-200 dark:border-neutral-700">
        <button
          onClick={onToggleCollapse}
          className="p-1.5 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded-lg transition-colors"
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <PanelRightClose className="h-4 w-4" />
          )}
        </button>
        {!collapsed && (
          <span className="text-xs font-medium text-slate-500 dark:text-neutral-400 uppercase tracking-wide">
            Insights
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Tabs */}
          {justUnlocked && (
            <div className="px-3 py-2 border-b border-slate-200 dark:border-neutral-700 bg-green-50 dark:bg-green-900/15 text-[11px] text-green-800 dark:text-green-200">
              ✅ Insights unlocked — Intel, Actions, Links, Scenarios, Graph, Timeline
            </div>
          )}
          <div className="flex border-b border-slate-200 dark:border-neutral-700">
            {visibleTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex flex-col items-center gap-1 py-2 text-xs transition-colors ${
                  activeTab === tab.id
                    ? 'text-amber-600 dark:text-amber-400 border-b-2 border-amber-500'
                    : 'text-slate-500 hover:text-slate-700 dark:text-neutral-400'
                }`}
                title={tab.label}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Locked features (not tabs) — makes unlock feel intentional */}
          {!notebookIsReady && lockedTabs.length > 0 && (
            <div className="px-3 py-2 border-b border-slate-200 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/10">
              <div className="text-[11px] text-slate-600 dark:text-neutral-300">
                🔒 Locked until the vault is {notebookIsLowQuality ? 'fixed' : 'ready'}.
                {/* ✅ ENTERPRISE V2: REMOVED "(X/Y analyzed)" display - misleading post-ingestion */}
              </div>
              {gateMessage && (
                <div className="mt-1 text-[11px] text-slate-500 dark:text-neutral-400">
                  {gateMessage}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {lockedTabs.map(t => (
                  <div
                    key={t.id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800/40 text-[11px] text-slate-600 dark:text-neutral-300"
                    title="Unlocks when the vault becomes Readable/Trusted"
                  >
                    <span>🔒</span>
                    <span>{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tab Content - SCROLLABLE AREA */}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {activeTab === 'preview' && (
              <FilePreviewContent
                file={freshSelectedFile}
                notebook={notebook}
                targetPage={targetPage}
                targetText={targetText}
                onClearSelectedFile={onClearSelectedFile}
              />
            )}
            {activeTab === 'intelligence' && notebookIsReady && (
              <IntelligenceViewContent
                data={intelligenceData}
                isLoading={isLoadingIntelligence}
                onAnalyze={runIntelligenceAnalysis}
                notebook={notebook}
              />
            )}
            {activeTab === 'actions' && notebookIsReady && (
              <ActionsViewContent
                data={actionsData}
                isLoading={isLoadingActions}
                onRefresh={loadActions}
                notebook={notebook}
              />
            )}
            {activeTab === 'crossdomain' && notebookIsReady && (
              <CrossDomainViewContent
                data={crossDomainData}
                isLoading={isLoadingCrossDomain}
                onRefresh={loadCrossDomain}
                notebook={notebook}
              />
            )}
            {activeTab === 'graph' && notebookIsReady && (
              <GraphViewContent
                graphData={graphData}
                isLoading={isLoadingGraph}
                onRefresh={loadGraph}
                onRebuild={rebuildGraph}
                notebook={notebook}
              />
            )}
            {activeTab === 'scenarios' && notebookIsReady && (
              <ScenariosViewContent
                scenariosData={scenariosData}
                isLoading={isLoadingScenarios}
                onGenerate={loadScenarios}
                notebook={notebook}
              />
            )}
            {activeTab === 'timeline' && notebookIsReady && (
              <TimelineViewContent
                timelineData={timelineData}
                isLoading={isLoadingTimeline}
                onBuild={onBuildTimeline}
                notebook={notebook}
              />
            )}
            {activeTab === 'tools' && (
              <ToolsContent notebook={notebook} onMapClauses={onMapClauses} />
            )}
          </div>
        </>
      )}

      {collapsed && (
        <div className="flex-1 flex flex-col">
          {/* Empty row with border to align with middle panel */}
          <div className="py-2 border-b border-slate-200 dark:border-neutral-700" />

          {/* Tab icons */}
          <div className="flex-1 flex flex-col items-center pt-2 space-y-2 overflow-y-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  onToggleCollapse();
                  setActiveTab(tab.id);
                }}
                className={`p-2 hover:bg-slate-200 dark:hover:bg-neutral-700 rounded-lg ${
                  activeTab === tab.id ? 'text-amber-600' : 'text-slate-500'
                }`}
                title={tab.label}
              >
                {tab.icon}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// File Preview Content - Shows actual file (PDF, image) or extracted text
const FilePreviewContent = ({ file, notebook, targetPage, targetText, onClearSelectedFile }) => {
  // ✅ FIX: ALL hooks must be at the top, BEFORE any early returns
  const [viewMode, setViewMode] = useState('file'); // 'file' | 'text'
  const userViewModeRef = useRef(false);
  const lastFileIdRef = useRef(null);
  const [extractedText, setExtractedText] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [canPreviewFile, setCanPreviewFile] = useState(false);
  const [iconError, setIconError] = useState(false); // ✅ MOVED: Was after early return, causing hooks error
  const [activeSheetKey, setActiveSheetKey] = useState(null);
  const iframeRef = useRef(null);
  const socketStatus = useSocketStatus();
  const emitSocket = useSocketEmit();

  // ✅ ENTERPRISE: Normalize API origin to avoid double "/api" issues across environments
  const apiOrigin = useMemo(() => {
    return getApiBaseUrl().replace(/\/api\/?$/, '').replace(/\/$/, '');
  }, []);

  // Get file type (safe with optional chaining)
  const fileName = file?.originalName || file?.name || file?.filename || '';
  const mimeType = file?.mimeType || '';

  // Check for large PDF
  const isLargePdf =
    file?.size > 10 * 1024 * 1024 &&
    (mimeType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf'));

  // Determine file type capabilities
  const isPdf = mimeType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');
  const isImage = mimeType.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
  const isWord = mimeType.includes('word') || /\.(doc|docx)$/i.test(fileName);
  const isPowerPoint =
    mimeType.includes('presentation') ||
    mimeType.includes('powerpoint') ||
    /\.(ppt|pptx)$/i.test(fileName);
  const isExcel =
    mimeType.includes('excel') ||
    mimeType.includes('spreadsheet') ||
    /\.(xls|xlsx)$/i.test(fileName);
  const isTextFile = mimeType.includes('text') || /\.(txt|md|csv)$/i.test(fileName);
  const isOcrEligible =
    typeof file?._isOCREligible === 'boolean'
      ? file._isOCREligible
      : isPdf || isImage;

  // Can we show the file directly in browser?
  const canShowInBrowser = isPdf || isImage || isTextFile;

  // ✅ FINAL UX CONTRACT: Canonical trust state gates AI-derived subtabs.
  const trustState = String(file?.userTrust?.state || '').toLowerCase();
  const isProcessing = trustState === 'processing' || trustState === 'reading'; // backward compat
  const isLowQuality = trustState === 'low_quality';

  useEffect(() => {
    if (file) {
      loadFile();
    } else {
      setFileUrl(null);
      setExtractedText(null);
      setCanPreviewFile(false);
    }
  }, [file?.fileId, file?.id, notebook?.id]);

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ WAVE 25: Auto-switch to File View when citation is clicked
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (file?.citationHighlight) {
      console.log('📍 [FilePreview] Citation highlight received:', file.citationHighlight);
      // Switch to file view for Excel/Word/PPT/PDF
      if (isExcel || isWord || isPowerPoint || isPdf || isImage) {
        setViewMode('file');
        setCanPreviewFile(true);
      }
      // Set the page if specified
      if (file.citationHighlight.page) {
        setCurrentPage(file.citationHighlight.page);
      }
    }
  }, [file?.citationHighlight, isExcel, isWord, isPowerPoint, isPdf, isImage]);

  // Navigate to target page when citation is clicked
  useEffect(() => {
    if (targetPage && targetPage > 0) {
      setCurrentPage(targetPage);
      // If file can be previewed, show file view; otherwise show text
      setViewMode(canShowInBrowser || isExcel || isWord || isPowerPoint ? 'file' : 'text');
      setCanPreviewFile(true);

      // If PDF in iframe, try to navigate to page
      if (isPdf && iframeRef.current && fileUrl) {
        // Update iframe src with page parameter
        const pageUrl = `${fileUrl}#page=${targetPage}`;
        if (iframeRef.current.src !== pageUrl) {
          iframeRef.current.src = pageUrl;
        }
      }
    }
  }, [targetPage, targetText, canShowInBrowser]);

  // Auto-select view mode based on file type (only on file change)
  useEffect(() => {
    const currentFileId = file?.fileId || file?.id || null;
    if (currentFileId && currentFileId !== lastFileIdRef.current) {
      lastFileIdRef.current = currentFileId;
      userViewModeRef.current = false; // reset user preference when switching files
    }
    if (file && !userViewModeRef.current) {
      // Reset icon error when file changes
      setIconError(false);

      // ✅ ENTERPRISE FIX: Enable preview for Excel, Word, PowerPoint using UnifiedDocumentViewer
      if (isExcel || isWord || isPowerPoint) {
        setViewMode('file'); // ✅ Enable file view for office documents
        setCanPreviewFile(true); // ✅ Enable the File View tab
      } else if (isLargePdf) {
        // Large PDFs default to text view to avoid browser hanging
        setViewMode('text');
        setCanPreviewFile(true); // Can still switch to file view if needed
      } else if (canShowInBrowser) {
        setViewMode('file');
        setCanPreviewFile(true);
      } else {
        setViewMode('text');
        setCanPreviewFile(false);
      }
    }
  }, [file, isWord, isPowerPoint, isExcel, isLargePdf, canShowInBrowser]);

  const setViewModeUser = useCallback((next) => {
    userViewModeRef.current = true;
    setViewMode(next);
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ TRUTH SYNC: WEBSOCKET LIVE-SYNC for TEXT_UPDATED / OCR_COMPLETED events
  // ═══════════════════════════════════════════════════════════════════════════════
  // ✅ FINAL UX CONTRACT: Do not expose engine/IDX/TXT to users. Keep contentMeta for gating only.
  const [ocrEngineInfo, setOcrEngineInfo] = useState(null); // internal only (kept for debug builds)
  const [contentMeta, setContentMeta] = useState(null); // ✅ ENTERPRISE: Canonical text metadata
  const [isRetryingOCR, setIsRetryingOCR] = useState(false);
  const lastUltraRecoverAtRef = useRef(null);
  const isAdmin = useMemo(() => {
    const user = authService?.getUser?.();
    const role = String(user?.role || '').toLowerCase();
    return role === 'admin' || role === 'super_admin';
  }, []);
  const adminDiagnostics = useMemo(() => {
    if (!file) return null;
    const structural = file.structuralConfidence || file.structural_confidence || null;
    const headerConf = structural?.header ?? null;
    const footerConf = structural?.footer ?? null;
    const ocrEngine = file.ocrEngine || ocrEngineInfo?.engine || null;
    const ocrTier = file.ocrTier || ocrEngineInfo?.tier || null;
    const ocrConfidence = file.ocrConfidence ?? null;
    const gcpcReuse = file.origin === 'global-cache';
    const gcpcQuarantine = file.gcpcQuarantine === true;
    const consensusTriggered = file.consensus?.triggered === true;
    const consensusSimilarity = file.consensus?.similarity ?? null;
    const businessFailures = []
      .concat(file?.stateWarnings || [])
      .concat(contentMeta?.corruptionIssues || [])
      .filter(Boolean);
    return {
      headerConf,
      footerConf,
      ocrEngine,
      ocrTier,
      ocrConfidence,
      gcpcReuse,
      gcpcQuarantine,
      consensusTriggered,
      consensusSimilarity,
      businessFailures,
    };
  }, [file, ocrEngineInfo, contentMeta]);

  const sheetDetails = useMemo(() => {
    const sheets = contentMeta?.canonicalMetadata?.sheetDetails || contentMeta?.sheetDetails || null;
    return Array.isArray(sheets) ? sheets : null;
  }, [contentMeta]);

  useEffect(() => {
    if (!Array.isArray(sheetDetails) || sheetDetails.length === 0) return;
    if (activeSheetKey) return;
    const first = sheetDetails[0];
    const key = first?.name || `Sheet_${Number(first?.index || 0) + 1}`;
    setActiveSheetKey(key);
  }, [sheetDetails, activeSheetKey]);

  const reloadExtractedText = useCallback(
    async (source = 'refresh') => {
      if (!file || !notebook?.id) return;
      const fileId = file.fileId || file.id;
      if (!fileId) return;

      try {
        const response = await intelligenceStudioApi.getFileContent(notebook.id, fileId);
        const data = response?.data?.data || response?.data || response;

        const content = data?.content || null;
        if (!content || file._textUnavailable) {
          setExtractedText(null);
          console.log(`⚠️ [FilePreview] Text unavailable (${source}) for ${fileId}`);
        } else {
          setExtractedText(content);
          console.log(`✅ [FilePreview] Extracted text refreshed (${source}) (${content.length} chars)`);
        }

        setOcrEngineInfo({
          engine: data?.ocrEngine || data?.engine || 'unknown',
          tier: data?.ocrQualityTier || null,
          textHealthPct: typeof data?.textHealthPct === 'number' ? data.textHealthPct : null,
          indexCoveragePct:
            typeof data?.indexCoveragePct === 'number' ? data.indexCoveragePct : null,
          verified: !!data?.verified,
        });

        setContentMeta({
          isCanonical: data?.isCanonical || false,
          contentSource: data?.contentSource || 'unknown',
          isCorrupted: data?.isCorrupted || false,
          corruptionIssues: data?.corruptionIssues || [],
          fileStatus: data?.fileStatus || null,
          needsRetryOCR: data?.needsRetryOCR || false,
          wasIndexed: data?.wasIndexed !== false,
          canonicalMetadata: data?.canonicalMetadata || null,
          sheetDetails: data?.sheetDetails || data?.canonicalMetadata?.sheetDetails || null,
          isArabicLegal: data?.isArabicLegal || data?.isArabicLegalDocument || false,
          textHealthPct: data?.textHealthPct || null,
          indexCoveragePct: data?.indexCoveragePct || null,
        });
      } catch (err) {
        console.warn(`⚠️ [FilePreview] Failed to reload extracted text (${source}):`, err.message);
      }
    },
    [file, notebook?.id]
  );
  useEffect(() => {
    if (!notebook?.id || socketStatus !== 'connected') return;
    emitSocket('subscribe', notebook.id);
    return () => {
      emitSocket('unsubscribe', notebook.id);
    };
  }, [notebook?.id, socketStatus, emitSocket]);

  useSocketEvent(
    'notebook_event',
    payload => {
      const { event, data } = payload || {};
      const fileId = file?.fileId || file?.id;
      if (!fileId || !notebook?.id) return;

      if (
        (event === 'text.updated' || event === 'ocr.completed') &&
        data?.fileId === fileId &&
        data?.notebookId === notebook.id
      ) {
        console.log(`📡 [FilePreview] ${event} received:`, data);
        reloadExtractedText('socket');
      }
    },
    {
      enabled: Boolean(notebook?.id && (file?.fileId || file?.id)),
    }
  );

  const loadFile = async () => {
    if (!file || !notebook?.id) return;

    setIsLoading(true);
    setError(null);
    setFileUrl(null); // ✅ Reset URL first

    const fileId = file.fileId || file.id;

    try {
      // ═══════════════════════════════════════════════════════════════════════════
      // FIX #4: CANONICAL TEXT ONLY (STRICT)
      // UI MUST read ONLY canonicalExtractedText from API - NO FALLBACKS to legacy fields
      // ═══════════════════════════════════════════════════════════════════════════
      // ✅ STEP 1: Always load extracted text FIRST (works for all file types)
      try {
        await reloadExtractedText('load');
      } catch (textErr) {
        console.warn('Could not fetch extracted text:', textErr.message);
        setExtractedText(null); // ✅ STRICT: No fallback, set to null
        // ✅ ENTERPRISE FIX: Don't block file preview if text fails
        // Office docs can still show file view via UnifiedDocumentViewer
        // Only set error if we can't show anything (no file preview AND no text)
        if (!canShowInBrowser && !isExcel && !isWord && !isPowerPoint) {
          setError('Could not load document text. The file may still be processing.');
        }
      }

      // ✅ STEP 2: Build file URL for preview (only for PDFs/images that can be shown in browser)
      if (canShowInBrowser) {
        // ✅ FIX: Add auth token to URL for PDF/image preview
        // ✅ ENTERPRISE FIX: Auth v2 stores token as 'auth_v2_accessToken'
        const token =
          authService?.getAccessToken?.() ||
          localStorage.getItem('auth_v2_accessToken') ||
          localStorage.getItem('accessToken') ||
          localStorage.getItem('authToken') ||
          '';
        const baseUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${notebook.id}/files/${fileId}/preview`;
        const query = new URLSearchParams();
        if (token) query.set('token', token);
        const previewUrl = `${baseUrl}${query.toString() ? `?${query.toString()}` : ''}`;
        query.set('proxy', '1');
        const proxyUrl = `${baseUrl}?${query.toString()}`;

        // ✅ FIX: Verify URL is accessible before setting (prevents initial error)
        try {
          const testResponse = await traceFetch(
            previewUrl,
            { method: 'HEAD', redirect: 'manual' },
            {
              apiName: 'file.preview.head',
              pipelineStage: 'http',
              notebookId: notebook?.id || null,
              fileId: fileId || null,
            }
          );
          const isRedirect = [301, 302, 303, 307, 308].includes(testResponse.status);
          if (testResponse.ok || isRedirect) {
            setFileUrl(previewUrl);
            setCanPreviewFile(true);
            console.log(`✅ [FilePreview] PDF/Image URL verified: ${fileId}`);
          } else {
            console.warn(
              `⚠️ [FilePreview] Preview URL HEAD check failed (${testResponse.status}), but will try anyway`
            );
            // ✅ ENTERPRISE FIX: fallback to proxy to avoid CORS/redirect failures
            try {
              const proxyResponse = await traceFetch(
                proxyUrl,
                { method: 'HEAD' },
                {
                  apiName: 'file.preview.proxy.head',
                  pipelineStage: 'http',
                  notebookId: notebook?.id || null,
                  fileId: fileId || null,
                }
              );
              if (proxyResponse.ok) {
                setFileUrl(proxyUrl);
                setCanPreviewFile(true);
              } else {
                setFileUrl(previewUrl);
                setCanPreviewFile(true);
              }
            } catch (_) {
              setFileUrl(previewUrl);
              setCanPreviewFile(true);
            }
            // Only switch to text if it's a critical server error (500+)
            if (testResponse.status >= 500) {
              setViewMode('text');
            }
          }
        } catch (urlErr) {
          console.warn(`⚠️ [FilePreview] Could not verify preview URL:`, urlErr.message);
          // Fallback to proxy to avoid cross-origin redirects
          setFileUrl(proxyUrl);
          setCanPreviewFile(true);
        }
      } else if (!isExcel && !isWord && !isPowerPoint) {
        // ✅ FIX: Don't reset canPreviewFile for office docs - they use UnifiedDocumentViewer
        setCanPreviewFile(false);
      }
    } catch (err) {
      console.error('Failed to load file:', err);
      setError('Failed to load file preview');
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Fallback: refresh extracted text after re-OCR completion
  useEffect(() => {
    const completedAt = file?._ultraRecoverCompletedAt || null;
    if (!completedAt || completedAt === lastUltraRecoverAtRef.current) return;
    lastUltraRecoverAtRef.current = completedAt;
    reloadExtractedText('ultra_recover');
  }, [file?._ultraRecoverCompletedAt, reloadExtractedText]);

  if (!file) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-center">
        <div>
          <FileText className="h-10 w-10 mx-auto text-slate-300 dark:text-neutral-600 mb-3" />
          <p className="text-sm text-slate-500 dark:text-neutral-400">
            Select a source from the left panel to preview
          </p>
          <p className="text-xs text-slate-400 mt-2">
            Or click on a 〔Source〕 citation in the chat
          </p>
        </div>
      </div>
    );
  }

  const displayName = file.originalName || file.name || file.filename || 'Document';
  const pageCount = file.pages || file.pageCount || 0;

  // File type icon - VS Code Icons for Office documents
  const CDN_BASE =
    'https://res-1.cdn.office.net/files/fabric-cdn-prod_20230815.002/assets/item-types/48';
  const VSCODE_ICONS_BASE =
    'https://raw.githubusercontent.com/vscode-icons/vscode-icons/master/icons';
  // iconError state is now declared at the top of the component

  const getFileIcon = () => {
    if (isPdf) return `${VSCODE_ICONS_BASE}/file_type_pdf.svg`; // VS Code Icons - has "PDF" text
    if (isWord) return `${VSCODE_ICONS_BASE}/file_type_word.svg`; // VS Code Icons
    if (isExcel) return `${VSCODE_ICONS_BASE}/file_type_excel.svg`; // VS Code Icons
    if (isPowerPoint) return `${VSCODE_ICONS_BASE}/file_type_powerpoint.svg`; // VS Code Icons
    if (isImage) return '🖼️'; // Keep emoji for images
    return '📁'; // Default fallback
  };

  const iconSrc = getFileIcon();
  const isCDNIcon = iconSrc.startsWith('http');

  // Get file extension for tooltip
  const fileExt = displayName.split('.').pop()?.toLowerCase() || '';
  const fileTooltip = getFileTypeTooltip(fileExt);

  // File type label
  const getFileTypeLabel = () => {
    if (isPdf) return 'PDF';
    if (isImage) return 'Image';
    if (isWord) return 'Word';
    if (isPowerPoint) return 'PowerPoint';
    if (isExcel) return 'Excel';
    if (isTextFile) return 'Text';
    return 'Document';
  };

  return (
    <div className="h-full flex flex-col">
      {/* File header */}
      <div className="flex items-center gap-3 p-3 border-b border-slate-200 dark:border-neutral-700 flex-shrink-0">
        <div
          className={`p-2 rounded-lg ${
            isPdf
              ? 'bg-red-100 dark:bg-red-900/30'
              : isImage
                ? 'bg-green-100 dark:bg-green-900/30'
                : isWord
                  ? 'bg-blue-100 dark:bg-blue-900/30'
                  : isPowerPoint
                    ? 'bg-orange-100 dark:bg-orange-900/30'
                    : isExcel
                      ? 'bg-amber-100 dark:bg-amber-900/30'
                      : 'bg-amber-100 dark:bg-amber-900/30'
          }`}
        >
          {isCDNIcon && !iconError ? (
            <img
              src={iconSrc}
              alt={getFileTypeLabel()}
              title={fileTooltip}
              className="w-6 h-6 object-contain"
              style={{ cursor: 'help' }}
              onError={() => setIconError(true)}
            />
          ) : (
            <span className="text-xl" title={fileTooltip} style={{ cursor: 'help' }}>
              {iconError || !isCDNIcon ? (isImage ? '🖼️' : '📁') : iconSrc}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-slate-900 dark:text-white truncate text-sm">
            {displayName}
          </h4>
          <p className="text-xs text-slate-500">
            {getFileTypeLabel()}
            {pageCount > 0 && ` • ${pageCount} pages`}
            {currentPage > 1 && ` • Page ${currentPage}`}
          </p>
        </div>
        {/* ✅ Action icons - Download, Expand, Close */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => {
              // Download file
              const downloadUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${notebook?.id}/files/${file?.fileId || file?.id}/download`;
              // ✅ ENTERPRISE FIX: Auth v2 stores token as 'auth_v2_accessToken'
              const token =
                localStorage.getItem('auth_v2_accessToken') ||
                localStorage.getItem('accessToken') ||
                localStorage.getItem('authToken');
              const link = document.createElement('a');
              link.href = `${downloadUrl}${token ? `?token=${encodeURIComponent(token)}` : ''}`;
              link.download = displayName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-neutral-700 text-slate-500 dark:text-neutral-400"
            title="Download file"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              // Open in new tab (expand)
              const previewUrl = `${apiOrigin}/api/intelligence-studio/notebooks/${notebook?.id}/files/${file?.fileId || file?.id}/preview`;
              // ✅ ENTERPRISE FIX: Auth v2 stores token as 'auth_v2_accessToken'
              const token =
                localStorage.getItem('auth_v2_accessToken') ||
                localStorage.getItem('accessToken') ||
                localStorage.getItem('authToken');
              window.open(
                `${previewUrl}${token ? `?token=${encodeURIComponent(token)}` : ''}`,
                '_blank'
              );
            }}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-neutral-700 text-slate-500 dark:text-neutral-400"
            title="Open in new tab"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onClearSelectedFile?.()}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-neutral-700 text-slate-500 dark:text-neutral-400"
            title="Close preview"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* View mode tabs */}
      <div className="flex border-b border-slate-200 dark:border-neutral-700 px-2">
        <button
          onClick={() => setViewModeUser('file')}
          disabled={!canPreviewFile}
          className={`px-3 py-2 text-xs font-medium transition-colors ${
            viewMode === 'file'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : !canPreviewFile
                ? 'text-slate-300 dark:text-neutral-600 cursor-not-allowed'
                : 'text-slate-500 hover:text-slate-700'
          }`}
          title={
            !canPreviewFile
              ? `${getFileTypeLabel()} files cannot be previewed directly`
              : 'View original file'
          }
        >
          📄 File View
        </button>
        <button
          onClick={() => setViewModeUser('text')}
          className={`px-3 py-2 text-xs font-medium transition-colors ${
            viewMode === 'text'
              ? 'text-amber-600 border-b-2 border-amber-500'
              : 'text-slate-500 hover:text-slate-700 dark:text-neutral-400 dark:hover:text-neutral-300'
          }`}
          title="View extracted text content"
        >
          📝 Extracted Text
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden min-h-0">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-amber-600 mb-3" />
            <p className="text-sm text-slate-500">Loading preview...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full">
            <AlertCircle className="h-8 w-8 text-amber-500 mb-2" />
            <p className="text-sm text-slate-500">{error}</p>
            <button onClick={loadFile} className="mt-2 text-xs text-amber-600 hover:underline">
              Try again
            </button>
          </div>
        ) : viewMode === 'file' ? (
          <div className="h-full w-full bg-slate-100 dark:bg-neutral-900">
            {isPdf && fileUrl ? (
              <>
                {/* ✅ FIX: Show warning for large PDFs */}
                {file?.size > 10 * 1024 * 1024 && (
                  <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Large PDF ({(file.size / (1024 * 1024)).toFixed(1)}MB) - If preview fails, use
                    "Text View"
                  </div>
                )}
                <iframe
                  ref={iframeRef}
                  src={`${fileUrl}#page=${currentPage}`}
                  className="w-full h-full border-0"
                  title={displayName}
                  onError={() => {
                    console.log('PDF iframe failed to load, switching to text view');
                    setViewModeUser('text');
                  }}
                />
              </>
            ) : isImage && fileUrl ? (
              <div className="h-full flex items-center justify-center p-4 overflow-auto">
                <img
                  src={fileUrl}
                  alt={displayName}
                  className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                  onError={() => {
                    console.warn('Image failed to load, switching to text view');
                    setViewModeUser('text');
                  }}
                />
              </div>
            ) : isTextFile && fileUrl ? (
              <iframe
                src={fileUrl}
                className="w-full h-full border-0 bg-white dark:bg-neutral-800"
                title={displayName}
              />
            ) : isExcel || isWord || isPowerPoint ? (
              /* ✅ ENTERPRISE: Excel, Word, PowerPoint native previews */
              <UnifiedDocumentViewer
                fileId={file?.fileId || file?.id}
                fileUrl={`${apiOrigin}/api/intelligence-studio/notebooks/${notebook?.id}/files/${file?.fileId || file?.id}`}
                fileType={isExcel ? 'excel' : isWord ? 'word' : 'powerpoint'}
                fileName={displayName}
                citations={[]}
                activeCitation={null}
                onCitationClick={() => {}}
                onClose={() => setViewModeUser('text')}
                hideHeader={
                  true
                } /* ✅ Hide duplicate header - already shown in FilePreviewContent */
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center p-6">
                {isCDNIcon && !iconError ? (
                  <img
                    src={iconSrc}
                    alt={getFileTypeLabel()}
                    title={fileTooltip}
                    className="w-16 h-16 mb-4 object-contain"
                    style={{ cursor: 'help' }}
                    onError={() => setIconError(true)}
                  />
                ) : (
                  <span className="text-5xl mb-4" title={fileTooltip} style={{ cursor: 'help' }}>
                    {iconError || !isCDNIcon ? (isImage ? '🖼️' : '📁') : iconSrc}
                  </span>
                )}
                <h3 className="font-medium text-slate-700 dark:text-neutral-300 mb-2">
                  {getFileTypeLabel()} Document
                </h3>
                <p className="text-sm text-slate-500 dark:text-neutral-400 mb-4 max-w-xs">
                  {isWord || isPowerPoint || isExcel
                    ? `${getFileTypeLabel()} files cannot be previewed directly in the browser. View the extracted text instead.`
                    : 'Preview not available for this file type'}
                </p>
                <button
                  onClick={() => setViewModeUser('text')}
                  className="px-4 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                >
                  📝 View Extracted Text
                </button>
              </div>
            )}
          </div>
        ) : isProcessing && !extractedText ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6">
            <div className="mb-3">
              <span
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200"
                title={
                  file?.userTrust?.tooltip ||
                  'Processing: extracting text, structure, and key signals…'
                }
              >
                <span>{file?.userTrust?.emoji || '🔄'}</span>
                <span>{file?.userTrust?.badge || 'Processing'}</span>
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-neutral-300">
              This document is still processing. Extracted text will appear when ready.
            </p>
          </div>
        ) : (
          <div className="h-full overflow-y-auto p-4 relative">
            {extractedText ? (
              <div className="bg-white dark:bg-neutral-800 rounded-lg p-4 shadow-sm">
                {/* ✅ FINAL UX CONTRACT: Low Quality banner (single truth) */}
                {file?.userTrust?.state === 'low_quality' && isOcrEligible && (
                  <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold mb-1">
                      <span>🔴</span>
                      <span>Low Quality</span>
                    </div>
                    <p className="text-sm text-red-700 dark:text-red-200">
                      {file?.userTrust?.tooltip ||
                        'Scan is too blurry for accurate analysis. Please upload a clearer version.'}
                    </p>
                  </div>
                )}

                {/* ✅ FINAL UX CONTRACT: Single Trust badge (no IDX/TXT/engine exposure) */}
                {file?.userTrust && (
                  <div className="mb-3">
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                        file.userTrust.state === 'trusted'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : file.userTrust.state === 'readable'
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                            : file.userTrust.state === 'low_quality'
                              ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                              : 'bg-slate-100 dark:bg-neutral-700 text-slate-700 dark:text-neutral-200'
                      }`}
                      title={file.userTrust.tooltip}
                    >
                      <span>{file.userTrust.emoji}</span>
                      <span>{file.userTrust.badge}</span>
                    </span>
                  </div>
                )}

                {/* ✅ FINAL UX CONTRACT: Retry OCR is exclusive to LOW QUALITY */}
                {isOcrEligible &&
                  file?.userTrust?.state === 'low_quality' &&
                  (contentMeta?.isCorrupted || contentMeta?.needsRetryOCR) && (
                    <div className="mb-3">
                      <button
                        onClick={async () => {
                          if (!notebook?.id || (!file?.fileId && !file?.id)) return;
                          setIsRetryingOCR(true);
                          try {
                            const fileId = file?.fileId || file?.id;
                            await intelligenceStudioApi.reprocessFile(notebook.id, fileId, {
                              reason: file?.retryOcrReason || 'Retry OCR (improve quality)',
                              tryCascade: true,
                              qualityThreshold: 85,
                            });
                            // Reload the file content after retry
                            loadFile();
                          } catch (err) {
                            console.error('Failed to retry OCR:', err);
                            alert('Failed to retry OCR: ' + (err.message || 'Unknown error'));
                          } finally {
                            setIsRetryingOCR(false);
                          }
                        }}
                        disabled={isRetryingOCR}
                        className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white text-sm rounded-lg flex items-center gap-2"
                        title={file?.retryOcrReason || 'Retry OCR to improve readability'}
                      >
                        {isRetryingOCR ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Retrying OCR...
                          </>
                        ) : (
                          <>🔄 Retry OCR</>
                        )}
                      </button>
                    </div>
                  )}

                {isAdmin && adminDiagnostics && (
                  <div className="mb-4 rounded-lg border border-slate-200 dark:border-neutral-700 p-3 text-xs text-slate-600 dark:text-neutral-300">
                    <div className="font-semibold text-slate-700 dark:text-neutral-200 mb-2">
                      Admin Diagnostics
                    </div>
                    <div>
                      Structural confidence — header: {adminDiagnostics.headerConf ?? '—'} | footer:{' '}
                      {adminDiagnostics.footerConf ?? '—'}
                    </div>
                    <div>
                      OCR tier — engine: {adminDiagnostics.ocrEngine || '—'} | tier:{' '}
                      {adminDiagnostics.ocrTier || '—'} | confidence:{' '}
                      {adminDiagnostics.ocrConfidence ?? '—'}
                    </div>
                    <div>
                      Header/footer suppression — summary/risk/qa: BODY_ONLY (suppressed)
                    </div>
                    <div>
                      GCPC — {adminDiagnostics.gcpcReuse ? 'reused' : 'regenerated'} | quarantine:{' '}
                      {adminDiagnostics.gcpcQuarantine ? 'yes' : 'no'}
                    </div>
                    <div>
                      Consensus — {adminDiagnostics.consensusTriggered ? 'triggered' : 'not triggered'}{' '}
                      {adminDiagnostics.consensusSimilarity !== null
                        ? `(sim=${Number(adminDiagnostics.consensusSimilarity).toFixed(3)})`
                        : ''}
                    </div>
                    <div>
                      Business rule failures:{' '}
                      {adminDiagnostics.businessFailures.length > 0
                        ? adminDiagnostics.businessFailures.slice(0, 4).join(' | ')
                        : 'none'}
                    </div>
                  </div>
                )}

                {isExcel && Array.isArray(sheetDetails) && sheetDetails.length > 0 ? (
                  <div>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {sheetDetails.map((sheet, idx) => {
                        const key = sheet?.name || `Sheet_${Number(sheet?.index || idx) + 1}`;
                        const isActive = key === activeSheetKey;
                        return (
                          <button
                            key={`${key}-${idx}`}
                            onClick={() => setActiveSheetKey(key)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
                              isActive
                                ? 'bg-amber-600 text-white border-amber-600'
                                : 'bg-white dark:bg-neutral-800 text-slate-700 dark:text-neutral-200 border-slate-200 dark:border-neutral-700 hover:bg-slate-50 dark:hover:bg-neutral-700'
                            }`}
                          >
                            {key}
                          </button>
                        );
                      })}
                    </div>
                    {(() => {
                      const active =
                        sheetDetails.find((sheet, idx) => {
                          const key = sheet?.name || `Sheet_${Number(sheet?.index || idx) + 1}`;
                          return key === activeSheetKey;
                        }) || sheetDetails[0];
                      const rows = Number(active?.rowCount || 0) || 0;
                      const cols = Number(active?.colCount || 0) || 0;
                      return (
                        <div className="border border-slate-200 dark:border-neutral-700 rounded-lg p-3">
                          <div className="text-xs font-semibold text-slate-600 dark:text-neutral-300 mb-2">
                            {(active?.name || 'Sheet') +
                              (rows > 0 || cols > 0 ? ` · ${rows}x${cols}` : '') +
                              (active?.hasFormulas ? ' · formulas' : '')}
                          </div>
                          <pre
                            dir="auto"
                            className="whitespace-pre-wrap text-sm text-slate-700 dark:text-neutral-300 leading-relaxed font-sans"
                          >
                            {active?.text || 'No extracted text available for this sheet.'}
                          </pre>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <pre
                    dir="auto"
                    className="whitespace-pre-wrap text-sm text-slate-700 dark:text-neutral-300 leading-relaxed font-sans"
                  >
                    {extractedText}
                  </pre>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full p-6">
                <FileText className="h-8 w-8 text-slate-300 dark:text-neutral-600 mb-3" />
                {/* ═══════════════════════════════════════════════════════════════════════════
                    FIX #4: CANONICAL TEXT ONLY (STRICT) - UI Placeholder
                    Show explicit message when text is unavailable
                ═══════════════════════════════════════════════════════════════════════════ */}
                <p className="text-sm text-slate-500 dark:text-neutral-400 mb-2 text-center">
                  {file?._textUnavailable
                    ? 'Text unavailable – requires reprocessing'
                    : isProcessing
                      ? 'Extracted text will appear when processing completes'
                      : 'No extracted text available'}
                </p>
                {/* ✅ ENTERPRISE: Show helpful action if file view is available */}
                {canPreviewFile && (isExcel || isWord || isPowerPoint || isPdf || isImage) && (
                  <button
                    onClick={() => setViewModeUser('file')}
                    className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm rounded-lg transition-colors"
                  >
                    📄 Switch to File View
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Page navigation for PDFs */}
      {isPdf && pageCount > 1 && viewMode === 'file' && (
        <div className="flex items-center justify-center gap-3 p-2 border-t border-slate-200 dark:border-neutral-700 bg-slate-50 dark:bg-neutral-800">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-neutral-700 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm text-slate-600 dark:text-neutral-400">
            Page {currentPage} of {pageCount}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(pageCount, p + 1))}
            disabled={currentPage >= pageCount}
            className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-neutral-700 disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
